// The org-config page's section taxonomy, kept out of the page component so the
// groups, the ?section= values, and the save-model split all have one home — and
// so the pure helpers here are importable (and testable) without pulling the
// 1900-line page in.
import { ConfigNavGroup } from "./ConfigSectionNav";

export type TabKey =
  | "general"
  | "urls"
  | "identifiers"
  | "behavior"
  | "citizen"
  | "lists"
  | "notes"
  | "notify"
  | "templates"
  | "exports"
  | "columns"
  | "json"
  | "api_keys"
  | "sso"
  | "directory"
  | "branding";

export const TAB_LABEL: Record<TabKey, string> = {
  general: "General",
  urls: "URLs",
  identifiers: "Identifiers",
  behavior: "Behaviour & flags",
  citizen: "Citizen site",
  lists: "Lists & options",
  notes: "Response notes",
  notify: "Notifications",
  templates: "Reply templates",
  exports: "Exports",
  columns: "Other columns",
  json: "JSON columns",
  api_keys: "API keys",
  sso: "SSO",
  directory: "Directory",
  branding: "Branding",
};

// Sidebar grouping. The first three groups are sections of ONE form over
// rv2_org_config sharing the Save/Reset footer; `savesIndependently` marks the
// group whose panels write on their own, so the footer can hide there instead of
// implying it would save them.
export const TAB_GROUPS: ConfigNavGroup<TabKey>[] = [
  {
    title: "Config",
    tabs: ["general", "urls", "identifiers", "behavior", "lists"],
  },
  {
    title: "Content",
    tabs: ["citizen", "notes", "templates", "notify"],
  },
  {
    title: "Integrations",
    tabs: ["exports", "columns", "json"],
  },
  {
    title: "Access",
    caption: "saves independently",
    savesIndependently: true,
    tabs: ["directory", "branding", "sso", "api_keys"],
  },
];

// Valid ?section= values, straight from the groups so a stale or hand-edited URL
// falls back to General instead of rendering an empty pane.
export const TAB_ORDER_SET = new Set<TabKey>(TAB_GROUPS.flatMap((g) => g.tabs));

/** ?section= -> a section, falling back to General for anything unrecognised so a
 *  stale or hand-edited URL can't render an empty pane. Exported for testing. */
export const resolveSection = (param: string | null | undefined): TabKey =>
  TAB_ORDER_SET.has(param as TabKey) ? (param as TabKey) : "general";

// Tabs whose panels own their own save/actions — the form footer is hidden while
// one of them is showing.
export const SELF_SAVING_TABS = new Set<TabKey>(
  TAB_GROUPS.filter((g) => g.savesIndependently).flatMap((g) => g.tabs),
);
